import * as React from "react";

import AppModals from "@app/AppModals";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@replo/design-system/components/sidebar/Sidebar";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { HomepageTutorialProvider } from "@v2/features/activation/components/HomepageTutorial";
import { AdminPanel } from "@v2/features/admin/AdminPanel";
import {
  useAdminPanelActions,
  useAdminPanelStore,
} from "@v2/features/admin/adminPanelStore";
import ManageChats from "@v2/features/chat/components/chat-elements/ManageChats";
import MobileChatHeader from "@v2/features/chat/components/MobileChatHeader";
import { useChatSidebar } from "@v2/features/chat/hooks/useChatSidebar";
import DashboardSidebar from "@v2/features/dashboard/sidebar/DashboardSidebar";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { routes } from "@v2/utils/router";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

function ChatRouteSidebarListener() {
  const { pathname } = useLocation();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isExpanded = isMobile ? openMobile : open;
  const shopsChatRoute = `${routes.shops.chat}/*`;
  const agentChatRoute = `${routes.agent.chat}/*`;

  useOnValueChange(pathname, (newPath, oldPath) => {
    const wasChat = Boolean(
      oldPath &&
        (matchPath(shopsChatRoute, oldPath) ||
          matchPath(agentChatRoute, oldPath)),
    );
    const isChat = Boolean(
      newPath &&
        (matchPath(shopsChatRoute, newPath) ||
          matchPath(agentChatRoute, newPath)),
    );
    if (!wasChat && isChat && isExpanded) {
      toggleSidebar();
    }
  });

  return null;
}

const DashboardLayout: React.FC<
  React.PropsWithChildren<{ contentIsCard: boolean }>
> = ({ children, contentIsCard }) => {
  const isDebugPanelVisible = useAdminPanelStore((s) => s.isVisible);
  const { toggle: toggleDebugMode } = useAdminPanelActions();
  useReploHotkeys({ debug: toggleDebugMode });
  const { pathname } = useLocation();

  const mobileLayoutRoutes = [
    routes.agent.home,
    routes.shops.home,
    routes.shops.products.list,
    routes.shops.products.edit,
    routes.shops.products.new,
    routes.shops.chat,
    routes.agent.chat,
  ];

  const isMobileLayoutRoute = mobileLayoutRoutes.some((route) => {
    return Boolean(matchPath(route, pathname));
  });

  const isShopsHome = Boolean(matchPath(routes.shops.home, pathname));
  const isAgentHome = Boolean(matchPath(routes.agent.home, pathname));
  const isHomePage = isShopsHome || isAgentHome;
  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  return (
    <div
      className={twMerge("flex flex-col", isMobile ? "h-[100dvh]" : "h-screen")}
    >
      <AppModals />
      {isDebugPanelVisible && <AdminPanel />}
      <div className="h-screen grow no-scrollbar overflow-y-scroll">
        <SidebarProvider className="h-full w-full">
          <HomepageTutorialProvider>
            <ChatRouteSidebarListener />
            <DashboardSidebar />
            {isMobile && (
              <>
                <MobileChatHeader />
                <Drawer
                  open={isChatSidebarOpen}
                  onOpenChange={setIsChatSidebarOpen}
                  title="All Chats"
                >
                  <ManageChats />
                </Drawer>
              </>
            )}

            <SidebarInset
              UNSAFE_className={twMerge(
                isMobileLayoutRoute && isMobile
                  ? "px-0 py-0 pt-14"
                  : "py-3 pr-3",
              )}
            >
              {contentIsCard ? (
                <div
                  className={twMerge(
                    "max-sm:border-0 max-sm:rounded-none max-sm:shadow-none shadow-2xs border-0.5 border-border no-scrollbar rounded-[10px] bg-white flex flex-col items-center h-full",
                    !isHomePage && "overflow-y-scroll",
                  )}
                >
                  <div
                    className={twMerge(
                      "no-scrollbar flex flex-col h-full w-full",
                      isHomePage
                        ? "items-stretch"
                        : "overflow-y-scroll p-6 pb-0 max-w-[1440px] items-center max-sm:p-0",
                    )}
                  >
                    <Outlet />
                    {children}
                  </div>
                </div>
              ) : (
                <>
                  <Outlet />
                  {children}
                </>
              )}
            </SidebarInset>
          </HomepageTutorialProvider>
        </SidebarProvider>
      </div>
    </div>
  );
};

export default DashboardLayout;
